import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { rowHeight, xToBeat } from './melody-roll-geometry.js'
import type { RollRange } from './melody-roll-geometry.js'

/**
 * What a drag on the roll does, as a pure function of the drag distance.
 *
 * Pointer handlers own three things - which note, where the drag started, and what kind of grab it
 * was - and nothing else. The arithmetic lives here so it can be tested without a layout engine,
 * and so all three drags land on the same `MelodyLib` edit ops the keyboard uses.
 *
 *   move   - the bar body. Onset snaps to the grid, pitch to a semitone.
 *   resize - the right edge. Duration snaps to the grid, capped at the next note.
 *   offset - the nub on the left edge. Moves the bar's DRAWN start off its gridline without
 *            touching `onset`, bounded to half a slot, and NOT snapped: the feel is continuous.
 *            This is the drag that edits the feel.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export type MelodyDragKind = 'move' | 'resize' | 'offset'

export interface MelodyDrag {
  kind: MelodyDragKind
  index: number
  /** Pointer position at grab time, as a percentage of the plot. */
  startXPercent: number
  startYPercent: number
}

export interface MelodyDragContext {
  range: RollRange
  totalBeats: number
}

/**
 * Apply a drag. `dxPercent`/`dyPercent` are the distance moved since the grab, as percentages of
 * the plot. Returns the melody untouched when the drag names a note that is no longer there.
 */
export const applyMelodyDrag = (
  melody: Melody,
  drag: MelodyDrag,
  dxPercent: number,
  dyPercent: number,
  context: MelodyDragContext
): Melody => {
  const note = melody.notes[drag.index]
  if (!note) {
    return melody
  }
  const deltaBeats = xToBeat(dxPercent, context.totalBeats)

  if (drag.kind === 'resize') {
    return MelodyLib.resizeMelodyNote(melody, drag.index, note.duration + deltaBeats)
  }
  if (drag.kind === 'offset') {
    return MelodyLib.setMelodyNoteOffset(
      melody,
      drag.index,
      note.offsetMs + deltaBeats * MelodyLib.beatMs(melody.tempo.bpm)
    )
  }
  const deltaSemitones = -Math.round(dyPercent / rowHeight(context.range))
  return MelodyLib.moveMelodyNote(melody, drag.index, {
    midi: note.midi + deltaSemitones,
    onset: note.onset + deltaBeats,
  })
}
